// User-preference storage. Persists to localStorage under a single key so we
// don't proliferate keys. Each helper is defensive against quota / SSR errors.
//
// Shape:
//   {
//     favorites: string[],     // ordered list of nav-item ids pinned to top
//     hidden: string[],        // nav-item ids the user wants hidden
//     defaultView: string,     // view id to open on app load (default: dashboard)
//     density: "comfortable" | "compact",
//   }

const KEY = "claimops:prefs";

const DEFAULTS = {
  favorites: [],
  hidden: [],
  defaultView: "dashboard",
  density: "comfortable",
};

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch (err) {
    console.error("prefs: read failed", err);
    return { ...DEFAULTS };
  }
}

function write(prefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
    window.dispatchEvent(new CustomEvent("prefschange", { detail: prefs }));
  } catch (err) {
    console.error("prefs: write failed", err);
  }
}

export function getPrefs() { return read(); }

export function setPrefs(patch) {
  const next = { ...read(), ...patch };
  write(next);
  return next;
}

export function toggleFavorite(id) {
  const cur = read();
  const exists = cur.favorites.includes(id);
  const favorites = exists ? cur.favorites.filter((x) => x !== id) : [...cur.favorites, id];
  return setPrefs({ favorites });
}

export function toggleHidden(id) {
  const cur = read();
  const exists = cur.hidden.includes(id);
  const hidden = exists ? cur.hidden.filter((x) => x !== id) : [...cur.hidden, id];
  return setPrefs({ hidden });
}

export function applyDensity(density) {
  const d = density === "compact" ? "compact" : "comfortable";
  document.documentElement.setAttribute("data-density", d);
}

export function initPrefs() {
  const p = read();
  applyDensity(p.density);
  return p;
}
